/**
 * MULTIPLAYER LOCAL — the couch lobby. Up to 4 players on one machine, on
 * controllers by default: the keyboard player starts REMOVED (add it back to
 * play 1 keyboard + up to 3 pads). Players claim a controller by pulling its
 * trigger (R2/RT). START spins up the same private bot room the Solo flow
 * uses; the extra seats join it as guest sessions and the match renders
 * split-screen (game/localPlayers.ts).
 *
 * R2 to join — NOT the A/Cross button: the menu pad-nav (main.ts padMenuLoop)
 * treats A as "click the focused control", so joining on A would also press
 * whatever the cursor sat on. R2 is inert in menus. Seated pads are NOT
 * claimed here (claiming happens per round, in LocalSeatView) so a seated
 * controller keeps navigating this panel.
 *
 * Match settings mirror the room lobby's set: 3D map preview + cycle, mode,
 * bots, difficulty, round time, vehicles, monsters, day/night, modifier.
 */
import { MAPS, MODIFIERS } from "@neon-purge/shared";
import type { Room } from "colyseus.js";
import { el } from "./dom.js";
import { showLoading, hideLoading } from "./loadingOverlay.js";
import { DIFFICULTIES, MODE_DESC, MODE_ORDER, fillRoundSelect, modeVoice } from "./modeInfo.js";
import { createMapPreview, type MapPreviewHandle } from "./mapPreview.js";
import { createSoloRoom } from "../net/game.js";
import { audio } from "../game/audio.js";
import { SEAT_STYLE, startLocalSession, type LocalSeat } from "../game/localPlayers.js";

export interface LocalActions {
  onJoined: (room: Room) => void;
  onBack: () => void;
}

const PREFS_KEY = "neonpurge.localMenu";
interface LocalPrefs {
  mode?: string; mapId?: string; bots?: string; difficulty?: string;
  roundS?: string; vehicles?: boolean; monsters?: boolean; night?: boolean; modifier?: string;
}

export function localScreen(actions: LocalActions): HTMLElement {
  const screen = el("div", { class: "screen play-screen local-screen" });
  const errorEl = el("div", { class: "error" });

  let prefs: LocalPrefs = {};
  try { prefs = JSON.parse(localStorage.getItem(PREFS_KEY) ?? "{}"); } catch { prefs = {}; }

  // ---- seats ----
  // Keyboard player OFF by default (owner request: couch = controllers).
  // With it off all four slots are pad seats; adding it back caps pads at 3.
  let keyboardOn = false;
  const seats: (Omit<LocalSeat, "label"> | null)[] = [null, null, null, null];
  const slotEls: HTMLElement[] = [];
  let p1Box: HTMLElement;

  const padSlots = (): number => (keyboardOn ? 3 : 4);
  const slotLabel = (i: number): string => `P${i + (keyboardOn ? 2 : 1)}`;

  const renderP1 = (): void => {
    if (keyboardOn) {
      const remove = el("button", { class: "ghost", text: "✕", title: "remove — controllers only (opens a 4th pad seat)" });
      remove.onclick = () => { keyboardOn = false; renderAll(); };
      p1Box.style.borderColor = "#00f0ff";
      p1Box.replaceChildren(
        el("div", { style: "color:#00f0ff;letter-spacing:0.14em;font-weight:700", text: "P1 READY" }),
        el("div", { class: "hint", text: "⌨ keyboard + mouse" }),
        remove,
      );
    } else {
      const add = el("button", { class: "ghost", text: "+ keyboard player" });
      add.onclick = () => {
        keyboardOn = true;
        // the 4th pad seat no longer exists — free it if someone had it
        if (seats[3]) seats[3] = null;
        renderAll();
      };
      p1Box.style.borderColor = "rgba(255,255,255,0.15)";
      p1Box.replaceChildren(
        el("div", { style: "letter-spacing:0.14em;color:var(--dim)", text: "⌨ KEYBOARD — OFF" }),
        el("div", { class: "hint", text: "controllers only" }),
        add,
      );
    }
  };

  const renderSlot = (i: number): void => {
    const style = SEAT_STYLE[i];
    const seat = seats[i];
    const box = slotEls[i];
    if (i >= padSlots()) {
      // blocked 4th slot while the keyboard player exists
      box.style.borderColor = "rgba(255,255,255,0.08)";
      box.style.opacity = "0.45";
      box.replaceChildren(
        el("div", { style: "letter-spacing:0.14em;color:var(--dim)", text: "SLOT LOCKED" }),
        el("div", { class: "hint", text: "remove the keyboard player to open" }),
      );
      return;
    }
    box.style.opacity = "1";
    box.style.borderColor = seat ? style.glow : "rgba(255,255,255,0.15)";
    if (seat) {
      const remove = el("button", { class: "ghost", text: "✕", title: "free this controller" });
      remove.onclick = () => {
        seats[i] = null;
        renderSlot(i);
        syncStart();
      };
      const padName = seat.padId.length > 34 ? `${seat.padId.slice(0, 34)}…` : seat.padId;
      box.replaceChildren(
        el("div", { style: `color:${style.glow};letter-spacing:0.14em;font-weight:700`, text: `${slotLabel(i)} READY` }),
        el("div", { class: "hint", text: `🎮 ${padName}` }),
        remove,
      );
    } else {
      box.replaceChildren(
        el("div", { style: "letter-spacing:0.14em;color:var(--dim)", text: `${slotLabel(i)} — OPEN` }),
        el("div", { class: "hint", text: "pull R2/RT on a controller to join" }),
      );
    }
  };

  const renderAll = (): void => {
    renderP1();
    for (let i = 0; i < seats.length; i++) renderSlot(i);
    syncStart();
  };

  const seatCount = (): number => seats.filter(Boolean).length;

  // ---- controller join detection: a fresh R2/RT pull claims a seat ----
  const prevTrig = new Map<number, boolean>();
  const poll = window.setInterval(() => {
    const pads = navigator.getGamepads?.() ?? [];
    for (const gp of pads) {
      if (!gp?.connected) continue;
      if (seats.some((s) => s && s.padIndex === gp.index)) continue;
      const b = gp.buttons[7];
      const pulled = !!b?.pressed || (b?.value ?? 0) > 0.55;
      const was = prevTrig.get(gp.index) ?? false;
      prevTrig.set(gp.index, pulled);
      if (!pulled || was) continue;
      const free = seats.findIndex((s, i) => s === null && i < padSlots());
      if (free < 0) continue;
      // NOT claimed here — the pad keeps driving the menu pad-nav so its
      // owner can reach START/Back. LocalSeatView claims it per round.
      seats[free] = { padIndex: gp.index, padId: gp.id, slot: free };
      audio.voice("gamestart", { interrupt: false });
      renderSlot(free);
      syncStart();
    }
    // a seated pad that got unplugged frees its seat
    for (let i = 0; i < seats.length; i++) {
      const s = seats[i];
      if (s && !pads[s.padIndex]?.connected) {
        seats[i] = null;
        renderSlot(i);
        syncStart();
      }
    }
  }, 120);

  // ---- MAP card: same 3D preview + cycle as the play screen / room lobby ----
  const RANDOM_MAP = { id: "__random__", name: "🎲 Random Map" };
  const mapCanvas = document.createElement("canvas");
  mapCanvas.className = "map3d";
  let preview: MapPreviewHandle | null = createMapPreview(mapCanvas);
  preview.start();
  const mapName = el("div", { class: "map3d-name" });
  const mapMeta = el("div", { class: "map3d-meta" });
  let mapId = prefs.mapId ?? Object.keys(MAPS)[0];

  const mapPool = (): { id: string; name: string }[] => {
    const mode = modeSelect.value;
    return [...Object.values(MAPS).filter((m) => m.modes.includes(mode)), RANDOM_MAP];
  };
  const setMap = (id: string, announce = false): void => {
    mapId = id;
    if (id === "__random__") {
      mapName.textContent = RANDOM_MAP.name;
      mapMeta.textContent = "spins the wheel — any map that fits the mode";
      preview?.showRandom();
      savePrefs();
      return;
    }
    const m = MAPS[id];
    if (!m) return;
    mapName.textContent = m.name;
    if (announce) audio.announceMap(m.id, m.name);
    const karts = (m.vehicles ?? []).filter((v) => v.kind === "kart").length;
    const helis = (m.vehicles ?? []).filter((v) => v.kind === "heli").length;
    mapMeta.textContent = `${m.theme === "day" ? "☀ Day" : "◈ Neon"}  ·  ${m.spawns.length} spawns  ·  🚗${karts} 🚁${helis}  ·  ${(m.jumpPads ?? []).length} pads`;
    preview?.loadMap(m);
    savePrefs();
  };
  const stepMap = (dir: number): void => {
    const pool = mapPool();
    const pos = pool.findIndex((m) => m.id === mapId);
    setMap(pool[((pos < 0 ? 0 : pos) + dir + pool.length) % pool.length].id, true);
  };
  const prevBtn = el("button", { class: "ghost nav", text: "‹" });
  const nextBtn = el("button", { class: "ghost nav", text: "›" });
  prevBtn.dataset.cyclePrev = ""; nextBtn.dataset.cycleNext = ""; // d-pad ←/→ targets
  prevBtn.onclick = () => stepMap(-1);
  nextBtn.onclick = () => stepMap(1);
  const mapCard = el("div", { class: "card map-card" },
    el("div", { class: "card-title", text: "MAP" }),
    el("div", { class: "map3d-wrap" }, mapCanvas, el("div", { class: "map3d-hint", text: "drag to rotate" })),
    el("div", { class: "map3d-bar", "data-pad-cycle-scope": "" }, prevBtn, el("div", { class: "map3d-info" }, mapName, mapMeta), nextBtn),
  );

  // ---- match settings (selects — the menu pad-nav steps them with ←/→) ----
  // Mode + modifier both get the one-line explanation the play screen and the
  // room lobby show under their pickers — before this the couch lobby was the
  // only place you chose a mode with no idea what it does. Same shared tables.
  const modeDescEl = el("div", { class: "hint", style: "margin:0.1rem 0 0.4rem;line-height:1.35" });
  const modDescEl = el("div", { class: "hint", style: "margin:0.1rem 0 0.2rem;line-height:1.35" });
  const refreshModeDesc = (): void => { modeDescEl.textContent = MODE_DESC[modeSelect.value] ?? ""; };
  const refreshModDesc = (): void => {
    modDescEl.textContent = modSelect.value
      ? (MODIFIERS.find((m) => m.id === modSelect.value)?.desc ?? "")
      : "No modifier — the mode's normal rules.";
  };

  const modeSelect = el("select", {}) as HTMLSelectElement;
  for (const [id, label] of MODE_ORDER) modeSelect.append(el("option", { value: id, text: label }));
  modeSelect.value = MODE_ORDER.some(([id]) => id === prefs.mode) ? prefs.mode! : "gungame";
  modeSelect.onchange = () => {
    audio.voice(modeVoice(modeSelect.value), { interrupt: true }); // announcer names the mode, like the online lobbies
    refreshModeDesc();
    const pool = mapPool();
    setMap(pool.some((m) => m.id === mapId) ? mapId : pool[0].id);
  };

  const botSelect = el("select", {}) as HTMLSelectElement;
  for (const n of [0, 1, 2, 3, 4, 5, 6, 8, 10, 12]) botSelect.append(el("option", { value: String(n), text: String(n) }));
  botSelect.value = prefs.bots ?? "3";
  // Same four difficulties, Title-Case labels and "V. Easy" default as the menu
  // and room lobbies — the couch lobby used to show raw lowercase easy/medium/
  // hard, omit Very Easy, and default a notch harder than everywhere else.
  const diffSelect = el("select", {}) as HTMLSelectElement;
  for (const d of DIFFICULTIES) diffSelect.append(new Option(d.label, d.id));
  diffSelect.value = prefs.difficulty ?? "veryeasy";
  const roundSelect = el("select", {}) as HTMLSelectElement;
  fillRoundSelect(roundSelect, prefs.roundS ?? "600");
  const modSelect = el("select", {}) as HTMLSelectElement;
  modSelect.append(el("option", { value: "", text: "Standard" }));
  for (const m of MODIFIERS) modSelect.append(el("option", { value: m.id, text: m.label }));
  modSelect.value = MODIFIERS.some((m) => m.id === prefs.modifier) ? prefs.modifier! : "";

  const vehiclesCheck = el("input", { type: "checkbox" }) as HTMLInputElement;
  vehiclesCheck.checked = prefs.vehicles !== false;
  const monstersCheck = el("input", { type: "checkbox" }) as HTMLInputElement;
  monstersCheck.checked = prefs.monsters === true;
  const nightCheck = el("input", { type: "checkbox" }) as HTMLInputElement;
  nightCheck.checked = prefs.night === true;

  const savePrefs = (): void => {
    try {
      localStorage.setItem(PREFS_KEY, JSON.stringify({
        mode: modeSelect.value, mapId, bots: botSelect.value, difficulty: diffSelect.value,
        roundS: roundSelect.value, vehicles: vehiclesCheck.checked,
        monsters: monstersCheck.checked, night: nightCheck.checked, modifier: modSelect.value,
      } satisfies LocalPrefs));
    } catch { /* storage disabled — fine */ }
  };
  for (const c of [botSelect, diffSelect, roundSelect, vehiclesCheck, monstersCheck, nightCheck]) c.onchange = savePrefs;
  modSelect.onchange = () => { // like the play screen: announce + explain on change
    audio.voice(MODIFIERS.find((m) => m.id === modSelect.value)?.voice ?? "standard", { interrupt: true });
    refreshModDesc();
    savePrefs();
  };

  const cleanup = (): void => {
    window.clearInterval(poll);
    preview?.dispose();
    preview = null;
  };

  // ---- start ----
  const startBtn = el("button", { class: "primary wide big", text: "START LOCAL MATCH", "data-pad-start": "" }) as HTMLButtonElement;
  const syncStart = (): void => {
    const pads = seatCount();
    const total = pads + (keyboardOn ? 1 : 0);
    startBtn.disabled = pads === 0;
    startBtn.textContent = pads === 0
      ? (keyboardOn ? "WAITING FOR PLAYER 2…" : "WAITING FOR A CONTROLLER…")
      : total === 1 ? "START — 1 PLAYER (FULLSCREEN)"
      : `START — ${total} PLAYERS SPLIT-SCREEN`;
  };

  startBtn.onclick = () => {
    const joined = seats.filter((s): s is Omit<LocalSeat, "label"> => s !== null);
    if (!joined.length) return;
    errorEl.textContent = "";
    screen.querySelectorAll("button").forEach((b) => (b.disabled = true));
    showLoading("CONNECTING");
    audio.voice("gamestart", { interrupt: true });
    savePrefs();
    // pointer lock now, while the click's transient activation is valid —
    // pointless without a keyboard/mouse player
    if (keyboardOn) {
      try { void (document.getElementById("game-canvas") as HTMLCanvasElement | null)?.requestPointerLock(); } catch { /* best-effort */ }
    }
    const mode = modeSelect.value;
    const pool = Object.values(MAPS).filter((m) => m.modes.includes(mode));
    const resolvedMap = mapId === "__random__"
      ? pool[Math.floor(Math.random() * pool.length)]?.id ?? Object.keys(MAPS)[0]
      : mapId;
    void createSoloRoom({
      mapId: resolvedMap,
      mode,
      bots: Number(botSelect.value) || 0,
      difficulty: diffSelect.value,
      roundTimeS: Number(roundSelect.value) || 0,
      vehicles: vehiclesCheck.checked,
      monsters: monstersCheck.checked,
      nightCycle: nightCheck.checked,
      modifier: modSelect.value || undefined,
      purgeTeam: mode === "purge" ? true : undefined,
      teams: mode === "plunder" ? true : undefined,
    })
      .then((room) => {
        // seat slots re-index to be contiguous (P3 alone becomes P2 — or P1
        // when the keyboard player is off)
        startLocalSession(
          room.roomId,
          joined.map((s, i) => ({ ...s, slot: i, label: `P${i + (keyboardOn ? 2 : 1)}` })),
          keyboardOn,
        );
        cleanup();
        actions.onJoined(room);
      })
      .catch((err) => {
        hideLoading();
        if (document.pointerLockElement) document.exitPointerLock();
        errorEl.textContent = (err as Error).message || "couldn't start the match";
        screen.querySelectorAll("button").forEach((b) => (b.disabled = false));
        syncStart();
      });
  };

  const backBtn = el("button", { class: "ghost wide mt1", text: "‹ Back" });
  backBtn.onclick = () => { cleanup(); actions.onBack(); };

  // ---- layout: two columns like the play screen ----
  const slotWrap = el("div", { class: "local-slots" });
  p1Box = el("div", { class: "card", style: "border:1px solid rgba(255,255,255,0.15);padding:0.7rem 0.9rem;" });
  slotWrap.append(p1Box);
  for (let i = 0; i < 4; i++) {
    const box = el("div", { class: "card", style: "border:1px solid rgba(255,255,255,0.15);padding:0.7rem 0.9rem;" });
    slotEls.push(box);
    slotWrap.append(box);
  }

  const seatsCard = el("div", { class: "card" },
    el("div", { class: "card-title", text: "PLAYERS" }),
    el("div", { class: "hint", style: "margin-bottom:0.5rem", text: "Pull R2/RT on a controller to claim a seat. Controllers only by default — add the keyboard player to mix in mouse aim." }),
    slotWrap,
  );

  const matchCard = el("div", { class: "card" },
    el("div", { class: "card-title", text: "MATCH" }),
    el("div", { class: "row" },
      el("div", {}, el("label", { text: "Mode" }), modeSelect),
      el("div", {}, el("label", { text: "Round time" }), roundSelect)),
    modeDescEl,
    el("div", { class: "row" },
      el("div", {}, el("label", { text: "AI bots" }), botSelect),
      el("div", {}, el("label", { text: "Difficulty" }), diffSelect)),
    el("div", { class: "row" },
      el("div", {}, el("label", { text: "Round modifier" }), modSelect)),
    modDescEl,
    el("label", { class: "check-line" }, vehiclesCheck, el("span", { text: "Vehicles" })),
    el("label", { class: "check-line" }, monstersCheck, el("span", { text: "Giant monsters" })),
    el("label", { class: "check-line" }, nightCheck, el("span", { text: "Day/night cycle" })),
  );

  const left = el("div", { class: "play-col" }, mapCard, el("div", { class: "actions" }, errorEl, startBtn, backBtn));
  const right = el("div", { class: "play-col" },
    el("div", { class: "brand", text: "MULTIPLAYER LOCAL" }),
    seatsCard, matchCard);
  screen.append(el("div", { class: "play-grid" }, left, right));

  renderAll();
  refreshModeDesc();
  refreshModDesc();
  // initial preview: respect the saved map if it fits the saved mode
  const pool0 = mapPool();
  setMap(pool0.some((m) => m.id === mapId) ? mapId : pool0[0].id);
  return screen;
}
